import { dpmm } from '@core/app/actions/beambox/constant';

import type { PaperSelection } from '../constants';
import { markBaseRadiusPx, paperSizes, PRINT_MARGIN_MM } from '../constants';
import type { BBox, ContourState } from '../store';

import { getContourLayerBBox } from './contourElements';
import { getPathBBox } from './measure';

/**
 * Pure sheet-layout geometry: how the full box, the grid, the alignment marks
 * and the paper are derived from the store state. Nothing here reads or writes
 * the store — callers pass the state (or the relevant slice) in.
 */

export interface MarkPosition {
  cx: number;
  cy: number;
}

/** The grid arrangement of the full box, the slice most layout selectors need */
export interface GridState {
  fullBBox: BBox | null;
  gridColumns: number;
  gridGapMm: number;
  gridRows: number;
}

const unionBBox = (a: BBox, b: BBox): BBox => {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);

  return {
    height: Math.max(a.y + a.height, b.y + b.height) - y,
    width: Math.max(a.x + a.width, b.x + b.width) - x,
    x,
    y,
  };
};

/**
 * The box the printed sheet is laid out around — the extent of what will be
 * cut, not of the printing contents:
 *
 * - outline mode: the traced contour path. It already encloses every opaque pixel
 *   plus the offset, while the contents bbox can be far larger, because an image
 *   carries its transparent padding into its bbox. The contents bbox stands in
 *   only until the first path has been traced.
 * - layer mode: the printing contents unioned with the chosen contour layer. That layer is
 *   normally inside the contents bbox already, making the union a no-op — but not
 *   when it sits outside the collected design: a cut layer generated by a
 *   previous run is excluded from the design layers (getContentsLayers), and a hidden layer is not
 *   collected either.
 *
 * Either way the marks land just outside the geometry that is actually cut, so
 * both cut sources place them identically for the same cut path.
 */
export const computeFullBBox = (state: ContourState & { printingContentsBBox: BBox | null }): BBox | null => {
  const { contourElements, contourLayerName, contourPathD, contourSource, printingContentsBBox } = state;

  if (contourSource === 'outline') return (contourPathD ? getPathBBox(contourPathD) : null) ?? printingContentsBBox;

  if (!printingContentsBBox || !contourLayerName) return printingContentsBBox;

  const contourBBox = getContourLayerBBox(contourElements, contourLayerName);

  return contourBBox ? unionBBox(printingContentsBBox, contourBBox) : printingContentsBBox;
};

/** Bounding box covering all grid copies, in canvas units (px) */
export const getGridBBox = (state: GridState): BBox | null => {
  const { fullBBox, gridColumns, gridGapMm, gridRows } = state;

  if (!fullBBox) return null;

  const gap = gridGapMm * dpmm;

  return {
    height: gridRows * fullBBox.height + (gridRows - 1) * gap,
    width: gridColumns * fullBBox.width + (gridColumns - 1) * gap,
    x: fullBBox.x,
    y: fullBBox.y,
  };
};

/**
 * Four alignment marks, one outside each corner of the given box (the whole
 * grid). All values are in canvas units (px).
 */
export const computeMarkPositions = (bbox: BBox): MarkPosition[] => {
  const { height, width, x, y } = bbox;
  const offset = markBaseRadiusPx;

  return [
    { cx: x - offset, cy: y - offset },
    { cx: x + width + offset, cy: y - offset },
    { cx: x - offset, cy: y + height + offset },
    { cx: x + width + offset, cy: y + height + offset },
  ];
};

/**
 * Marks around the current grid; empty without a full bbox. The full box is
 * already the cut path's extent, so the marks only clear it by their own base
 * radius — everything that moves the cut path moves the full box with it.
 */
export const computeMarks = (state: GridState): MarkPosition[] => {
  const gridBBox = getGridBBox(state);

  return gridBBox ? computeMarkPositions(gridBBox) : [];
};

/** Offset of every grid copy relative to the first, in canvas units (px); the original (0, 0) comes first */
export const getGridOffsets = (state: GridState): Array<{ dx: number; dy: number }> => {
  const { fullBBox, gridColumns, gridGapMm, gridRows } = state;

  if (!fullBBox) return [{ dx: 0, dy: 0 }];

  const pitchX = fullBBox.width + gridGapMm * dpmm;
  const pitchY = fullBBox.height + gridGapMm * dpmm;
  const offsets: Array<{ dx: number; dy: number }> = [];

  for (let row = 0; row < gridRows; row += 1) {
    for (let column = 0; column < gridColumns; column += 1) {
      offsets.push({ dx: column * pitchX, dy: row * pitchY });
    }
  }

  return offsets;
};

/**
 * Bounding box of the given box plus the alignment marks (including their
 * white base discs), in canvas units (px).
 */
export const getContentBBox = (bbox: BBox, marks: MarkPosition[]): BBox => {
  let { x: minX, y: minY } = bbox;
  let maxX = bbox.x + bbox.width;
  let maxY = bbox.y + bbox.height;

  marks.forEach(({ cx, cy }) => {
    minX = Math.min(minX, cx - markBaseRadiusPx);
    minY = Math.min(minY, cy - markBaseRadiusPx);
    maxX = Math.max(maxX, cx + markBaseRadiusPx);
    maxY = Math.max(maxY, cy + markBaseRadiusPx);
  });

  return { height: maxY - minY, width: maxX - minX, x: minX, y: minY };
};

/** Bounding box of the laid out grid plus its alignment marks, in canvas units (px) */
export const getContentBBoxFromState = (state: {
  fullBBox: BBox | null;
  markPositions: MarkPosition[];
}): BBox | null => {
  if (!state.fullBBox) return null;

  return getContentBBox(state.fullBBox, state.markPositions);
};

/**
 * Oriented paper dimensions in mm. With 'fit', the paper wraps the content
 * (design + marks) plus the print margins, so orientation does not apply.
 */
export const getPaperDimensionsMm = (state: {
  fullBBox: BBox | null;
  markPositions: MarkPosition[];
  orientation: 'landscape' | 'portrait';
  paperKey: PaperSelection;
}): { heightMm: number; widthMm: number } => {
  if (state.paperKey === 'fit') {
    const contentBBox = getContentBBoxFromState(state);

    if (contentBBox) {
      return {
        heightMm: Math.ceil(contentBBox.height / dpmm + 2 * PRINT_MARGIN_MM),
        widthMm: Math.ceil(contentBBox.width / dpmm + 2 * PRINT_MARGIN_MM),
      };
    }
  }

  const { heightMm, widthMm } = paperSizes[state.paperKey === 'fit' ? 'a4' : state.paperKey];

  return state.orientation === 'portrait' ? { heightMm, widthMm } : { heightMm: widthMm, widthMm: heightMm };
};

/** Paper rectangle centered on the content, in canvas units (px) */
export const getPaperRect = (state: {
  fullBBox: BBox | null;
  markPositions: MarkPosition[];
  orientation: 'landscape' | 'portrait';
  paperKey: PaperSelection;
}): BBox | null => {
  const contentBBox = getContentBBoxFromState(state);

  if (!contentBBox) return null;

  const { heightMm, widthMm } = getPaperDimensionsMm(state);
  const width = widthMm * dpmm;
  const height = heightMm * dpmm;

  return {
    height,
    width,
    x: contentBBox.x + contentBBox.width / 2 - width / 2,
    y: contentBBox.y + contentBBox.height / 2 - height / 2,
  };
};
